import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as AttributesActions from '../../../../common/actions/attributes-actions';
import { Attribute } from '../../../../common/models/attribute';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface AttributesOverviewProps {
    onEdit: (unit: Attribute) => void;
}

const columns: PaloColumn<Attribute>[] = [
    {
        key: 'name',
        type: 'name',
        head: <FormattedMessage id="app.table.column.name" />,
        cell: attribute => attribute.name,
    },
];

export const AttributesOverview = (props: AttributesOverviewProps) => {
    const dispatch = useThunkDispatch();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const attributes = useAppSelector(state => state.attributes);
    const [searchParams, setSearchParams] = useSearchParams();

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    useEffect(() => {
        const attributesSearchParams = new URLSearchParams(searchParams);
        attributesSearchParams.set('locale', selectedLanguage);
        dispatch(AttributesActions.fetchAttributes(attributesSearchParams));
    }, [dispatch, searchParams, selectedLanguage]);

    const handleDelete = (attribute: Attribute) => {
        if (attribute.id && attribute.id >= 0) {
            dispatch(AttributesActions.deleteAttribute(attribute.id));
        }
    };

    return (
        <PaloListView<Attribute, number>
            embedded={true}
            columns={columns}
            items={attributes.items}
            getRowId={attribute => attribute.id}
            emptyLabel="Keine Attribute."
            rowActions={attribute => [
                { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(attribute) },
                { label: <FormattedMessage id="app.common.delete" />, onClick: () => handleDelete(attribute) },
            ]}
            pagination={{
                count: attributes.total,
                page: Number(searchParams.get('page') ?? 1),
                limit: Number(searchParams.get('limit') ?? 50),
                onPageChange: page => updateParams({ page: String(page) }),
                onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
            }}
        />
    );
};
